import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/Achievements.css';

/* ── tier config ── */
/* trophy SVG colored per tier */
const TrophySvg = ({ color = 'currentColor', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M7 4V2h10v2h3a1 1 0 011 1v3c0 2.5-1.5 4.3-3.7 4.9-.5 1.7-1.7 3-3.3 3.6V18h3v2H7v-2h3v-2.5c-1.6-.6-2.8-1.9-3.3-3.6C4.5 11.3 3 9.5 3 7V4a1 1 0 011-1h3zm0 2H5v1c0 1.6.8 2.8 2 3.3V6zm10 0v4.3c1.2-.5 2-1.7 2-3.3V6h-2z"/>
  </svg>
);

const TIER_COLORS = {
  'tier-gold':   '#d4a017',
  'tier-silver': '#8a9bb0',
  'tier-bronze': '#b87333',
  'tier-notable': '#3cb4e5',
};

const TIERS = [
  { key: 'champions',  label: 'Champions',          colorClass: 'tier-gold'   },
  { key: 'runners_up', label: 'Runners-Up',         colorClass: 'tier-silver' },
  { key: 'bronze',     label: 'Bronze',             colorClass: 'tier-bronze' },
  { key: 'notable',    label: 'Notable Placements', colorClass: 'tier-notable'},
];

/* ── tiny image carousel ── */
const CardGallery = ({ images }) => {
  const [active, setActive] = useState(0);
  const next = useCallback(() => setActive(p => (p + 1) % images.length), [images.length]);

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [images.length, next]);

  if (!images || images.length === 0) return null;
  return (
    <div className="ach-gallery">
      {images.map((img, i) => (
        <img key={i} src={`/uploads/achievements/${img}`} alt="" className={`ach-gallery-img ${i === active ? 'active' : ''}`} />
      ))}
    </div>
  );
};

/* ── single achievement card ── */
const AchievementCard = ({ item, tierConfig, index }) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const hasImages = item.images && item.images.length > 0;
  const yearLabel = item.date ? (() => { const d = new Date(item.date); return isNaN(d.getTime()) ? '' : d.getFullYear(); })() : '';
  const rankDisplay = item.rank ? `#${item.rank}` : '';

  return (
    <motion.div
      ref={ref}
      className={`ach-card ${tierConfig.colorClass}`}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.06 }}
    >
      {/* trophy icon + rank */}
      <div className="ach-card-top">
        <span className="ach-card-trophy"><TrophySvg color={TIER_COLORS[tierConfig.colorClass]} size={22} /></span>
        {rankDisplay && <span className="ach-card-rank">{rankDisplay}</span>}
      </div>

      {/* image (if any) */}
      {hasImages && (
        <div className="ach-card-image">
          <CardGallery images={item.images} />
        </div>
      )}

      {/* text */}
      <h3 className="ach-card-title">{item.title}</h3>
      {item.description && <p className="ach-card-desc">{item.description}</p>}

      {/* badges: year + prize */}
      <div className="ach-card-badges">
        {yearLabel && (
          <span className="ach-badge">{yearLabel}</span>
        )}
        {item.prize && (
          <span className="ach-badge ach-badge-prize">$ {item.prize}</span>
        )}
      </div>
    </motion.div>
  );
};

/* ── main ── */
const Achievements = ({ lang }) => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch('/api/achievements')
      .then(r => r.json())
      .then(d => { if (d.achievements) setData(d.achievements); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  // Group by tier
  const grouped = {};
  TIERS.forEach(t => { grouped[t.key] = []; });
  data.forEach(a => {
    const tier = a.tier || 'notable';
    if (!grouped[tier]) grouped[tier] = [];
    grouped[tier].push(a);
  });

  // Stats
  const countByTier = (key) => grouped[key]?.length || 0;
  const totalPrize = data.reduce((sum, a) => {
    if (!a.prize) return sum;
    const num = parseFloat(String(a.prize).replace(/[^0-9.]/g, ''));
    return sum + (isNaN(num) ? 0 : num);
  }, 0);
  const ctfsPlayed = data.length;

  if (!loaded) {
    return (
      <section className="achievements section" id="achievements" style={{ minHeight: '100vh' }}>
        <div className="container">
          <div className="ach-header">
            <div className="terminal-section-header" style={{ margin: '0 auto' }}>
              <div className="terminal-section-titlebar">root@ispclub:~/# cd /ispclub/achievements</div>
              <div className="terminal-section-body">
                <span className="terminal-sh-line">&gt;_ sh achievements.sh</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="achievements section" id="achievements" ref={ref} style={{ minHeight: '100vh' }}>
      <div className="container">
        {/* header */}
        <motion.div className="ach-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="terminal-section-header" style={{ margin: '0 auto' }}>
            <div className="terminal-section-titlebar">root@ispclub:~/# cd /ispclub/achievements</div>
            <div className="terminal-section-body">
              <span className="terminal-sh-line">&gt;_ sh achievements.sh</span>
            </div>
          </div>
          <h2 className="ach-main-title">{lang.ACHIEVEMENTS_TITLE || 'Hall of Fame'}</h2>
          <p className="ach-subtitle">{lang.ACHIEVEMENTS_SUBTITLE || 'Our victories speak for themselves.'}</p>
          <div className="ach-divider" />
        </motion.div>

        {/* stats */}
        {data.length > 0 && (
          <motion.div className="ach-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className="ach-stat tier-gold">
              <span className="ach-stat-icon"><TrophySvg color="#d4a017" size={24} /></span>
              <span className="ach-stat-value">{countByTier('champions')}</span>
              <span className="ach-stat-label">1ST PLACE</span>
            </div>
            <div className="ach-stat tier-silver">
              <span className="ach-stat-icon"><TrophySvg color="#8a9bb0" size={24} /></span>
              <span className="ach-stat-value">{countByTier('runners_up')}</span>
              <span className="ach-stat-label">2ND PLACE</span>
            </div>
            <div className="ach-stat tier-notable">
              <span className="ach-stat-icon">🔥</span>
              <span className="ach-stat-value">{ctfsPlayed}</span>
              <span className="ach-stat-label">CTFS PLAYED</span>
            </div>
            {totalPrize > 0 && (
              <div className="ach-stat tier-gold">
                <span className="ach-stat-icon">💰</span>
                <span className="ach-stat-value">{totalPrize >= 1000 ? `${Math.round(totalPrize / 1000)}K` : `$${totalPrize}`}</span>
                <span className="ach-stat-label">PRIZE MONEY</span>
              </div>
            )}
          </motion.div>
        )}

        {/* empty */}
        {data.length === 0 && (
          <div className="ach-empty">
            <div className="ach-empty-icon">🏆</div>
            <p>{lang.ACHIEVEMENTS_EMPTY || 'Chưa có thành tích nào.'}</p>
          </div>
        )}

        {/* tier sections */}
        {TIERS.map(tier => {
          const items = grouped[tier.key];
          if (!items || items.length === 0) return null;
          return (
            <motion.div key={tier.key} className="ach-tier-section"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className={`ach-tier-title ${tier.colorClass}`}>
                <span className="ach-tier-icon"><TrophySvg color={TIER_COLORS[tier.colorClass]} size={18} /></span>
                <span>{tier.label}</span>
              </h3>
              <div className="ach-tier-line" />
              <div className="ach-grid">
                {items.map((item, idx) => (
                  <AchievementCard key={item.id || idx} item={item} tierConfig={tier} index={idx} />
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default Achievements;
